import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Checkbox from "components/checkbox";
import { FiMail, FiLock, FiUser, FiShield } from "react-icons/fi";
import AuthService from "../../service/auth.service";
import { Spinner } from "../../components/loaders/Spinner.js";
import InputWithIcon from "../../components/input/InputWithIcon";

export default function SignIn() {
  const [email_user, setEmailUser] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [greeting, setGreeting] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = new URLSearchParams(location.search).get("redirect");

  useEffect(() => {
    setIsFormValid(email_user.trim() !== "" && password.trim() !== "");
  }, [email_user, password]);

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) setGreeting("Bonjour");
    else if (hour >= 12 && hour < 18) setGreeting("Bon après-midi");
    else setGreeting("Bonsoir");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email_user.trim() || !password.trim()) {
      setError("Veuillez remplir tous les champs");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await AuthService.login({ email_user, password });
      navigate(redirect || "/admin/default", { replace: true });
    } catch (error) {
      setError(
        error.response?.data?.message ||
        "Échec de la connexion. Veuillez vérifier vos identifiants."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex bg-gray-50 dark:bg-navy-900">
      {/* Left Side */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center relative overflow-hidden bg-gradient-to-br from-[#57A863] to-[#2E5A33]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
        </div>

        <div className="relative flex flex-col h-full items-center justify-center px-12 z-10">
          <div className="w-24 h-24 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center mb-8 shadow-lg">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
            </svg>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">AENAT ADMIN</h1>
          <p className="text-xl text-white/80 mb-12 max-w-md text-center">
            Plateforme de gestion pour l'Association des Étudiants et Anciens de la Faculté
          </p>

          <div className="grid grid-cols-2 gap-6 mt-8 w-full max-w-md">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4">
              <div className="text-4xl font-bold text-white mb-1">200+</div>
              <div className="text-sm text-white/80">Membres actifs</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4">
              <div className="text-4xl font-bold text-white mb-1">50+</div>
              <div className="text-sm text-white/80">Projets réalisés</div>
            </div>
          </div>

          <div className="absolute top-[20%] right-[10%] w-16 h-16 rounded-full bg-white opacity-10 animate-float"></div>
          <div className="absolute bottom-[20%] left-[10%] w-24 h-24 rounded-full bg-white opacity-5 animate-float-slow"></div>
        </div>
      </div>

      {/* Right Side */}
      <div className="w-full lg:w-1/2 items-center justify-center relative z-10">
        
        <div className="flex min-h-screen items-center justify-center px-6 py-12 lg:px-8 relative">
          <div className="w-full max-w-md space-y-8 dark:bg-navy-800 p-8 rounded-2xl relative z-10 max-h-[95vh] overflow-y-auto">
                       <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">{greeting} !</h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Connectez-vous à votre espace administrateur</p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-5">
                <div className="transition-all duration-300 hover:-translate-y-1">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Adresse Email ou nom d'utilisateur
                  </label>
                  <InputWithIcon
                    icon={FiMail}
                    placeholder="aenat.uf@gmail.com"
                    id="email_user"
                    type="text"
                    value={email_user}
                    onChange={(e) => setEmailUser(e.target.value)}
                    disabled={loading}
                    required
                    className="focus:border-[#57A863] focus:ring-[#57A863] transition-all duration-300 hover:shadow-md"
                  />
                </div>

                <div className="transition-all duration-300 hover:-translate-y-1">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Mot de passe
                  </label>
                  <InputWithIcon
                    icon={FiLock}
                    placeholder="••••••••"
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                    className="focus:border-[#57A863] focus:ring-[#57A863] transition-all duration-300 hover:shadow-md"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                    customClassName="text-[#57A863] focus:ring-[#57A863] border-2"
                  />
                  <label className={`ml-2 text-sm ${loading ? "text-gray-400" : "text-gray-700 dark:text-gray-300"}`}>
                    Se souvenir de moi
                  </label>
                </div>
                <div className="text-sm">
                  <Link
                    to="/"
                    className={`font-medium transition-colors ${loading ? "pointer-events-none text-[#57A863]/50" : "text-[#57A863] hover:text-[#2E5A33]"}`}
                    tabIndex={loading ? -1 : 0}
                  >
                    Mot de passe oublié?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="group relative flex h-12 w-full items-center justify-center rounded-lg bg-gradient-to-r from-[#57A863] to-[#2E5A33] px-4 py-3 text-sm font-semibold text-white transition-all hover:from-[#2E5A33] hover:to-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed transform hover:-translate-y-1 active:translate-y-0 hover:shadow-lg"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <Spinner size="sm" className="mr-2" />
                    <span>Connexion en cours...</span>
                  </div>
                ) : (
                  <>
                    <FiShield className="mr-2 h-5 w-5" />
                    <span>Se connecter</span>
                  </>
                )}
              </button>

              {error && (
                <div className="mt-4 rounded-md bg-red-50 dark:bg-red-900/30 p-3 text-center text-sm text-red-600 dark:text-red-400 animate-pulse">
                  {error}
                </div>
              )}

              <div className="mt-6 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                <FiUser className="mr-1 h-3 w-3" />
                <span>Connexion sécurisée avec authentification 2FA</span>
              </div>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                &copy; {new Date().getFullYear()} AENAT. Tous droits réservés.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }

        @keyframes float-slow {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0px); }
        }

        .animate-float {
          animation: float 4s ease-in-out infinite;
        }

        .animate-float-slow {
          animation: float-slow 7s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
